import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html } from 'lit';
import { timer, from, of, exhaustMap, catchError } from 'rxjs';
import { getCallResults } from '../../db/call-result-resolvers.js';
import { CALL_RESULT_LABELS, type CallResult } from '../../db/call-result-labels.js';

// Rows request their own IDs (including older pages); same-tick requests share one operation.
const waiting = new Map<string, ((value: CallResult | null) => void)[]>();
let scheduled = false;
function requestResult(id: string): Promise<CallResult | null> {
  return new Promise(resolve => {
    waiting.set(id, [...(waiting.get(id) ?? []), resolve]);
    if (scheduled) return;
    scheduled = true;
    setTimeout(() => {
      scheduled = false;
      const batch = [...waiting.entries()];
      waiting.clear();
      for (let i = 0; i < batch.length; i += 100) {
        const chunk = batch.slice(i, i + 100);
        const run = getCallResults as unknown as (ids: string[]) => () => Promise<string>;
        void Promise.resolve()
          .then(() => run(chunk.map(([key]) => key))())
          .then(raw => {
            const results = JSON.parse(raw);
            for (const [key, callbacks] of chunk) callbacks.forEach(cb => cb(results[key] ?? null));
          })
          .catch(() => {
            for (const [, callbacks] of chunk) callbacks.forEach(cb => cb(null));
          });
      }
    }, 0);
  });
}

export const CallResultBadge = stateful(({ callID }: { callID: string }) => {
  const query = useQuery(
    () => timer(0, 10000).pipe(exhaustMap(() => from(requestResult(callID)).pipe(catchError(() => of(null))))),
    [callID],
  );
  const result = query.value;
  const labels =
    result?.state === 'succeeded'
      ? result.actions.map(a => CALL_RESULT_LABELS[a])
      : [
          !result
            ? query.loading
              ? 'Loading…'
              : 'Status unavailable'
            : result.state === 'in-progress'
              ? 'In progress'
              : result.state === 'transfer-pending'
                ? 'Transfer pending'
                : result.state === 'transfer-unconfirmed'
                  ? 'Transfer unconfirmed'
                  : result.state === 'no-conversation'
                    ? 'No conversation'
                    : 'Unsuccessful',
        ];
  const color = result?.state === 'succeeded' ? '#15803d' : result?.state === 'unsuccessful' ? '#b91c1c' : '#475569';
  const title = result?.actions.includes('forwarded-browser')
    ? 'Browser test: redirect_call executed; no real telephone connection.'
    : result?.state === 'transfer-unconfirmed'
      ? 'Transfer was requested, but no connection confirmation was recorded.'
      : result?.state === 'unsuccessful'
        ? 'No confirmed ticket creation, ticket action or transfer was recorded.'
        : result?.actions.includes('closure-requested')
          ? 'The CRM accepted a closure request; the ticket is not confirmed closed.'
          : result?.state === 'no-conversation'
            ? 'Greeting only; no caller speech or technical failure was recorded.'
            : labels.join(', ');
  return html`<span class="flex flex-col gap-1" role="status" title=${title}>
    ${[...new Set(labels)].map(
      label =>
        html`<span
          style=${`background:${color};color:#fff;border-radius:6px;padding:4px 9px;font-size:12px;font-weight:600;white-space:nowrap;`}
          >${label}</span
        >`,
    )}
  </span>`;
});
